import axiosInstanceToken from '@/lib/axiosInstanceToken';
import type {
  Volume,
  VolumeSnapshot,
  SnapshotSchedule,
  StorageClass,
  VolumeActivityLog,
  MetricsHistoryPoint,
  CreateVolumePayload,
  ResizeVolumePayload,
  AttachVolumePayload,
  DetachVolumePayload,
  CloneVolumePayload,
  CreateSnapshotPayload,
  RestoreSnapshotPayload,
  CreateSchedulePayload,
  ListVolumesResponse,
  ListSnapshotsResponse,
  ListSchedulesResponse,
  VolumeOperationResponse,
} from './volume-type';

const getBaseUrl = () => axiosInstanceToken.defaults.baseURL || '';

// Volume API Functions
export const listVolumes = async (token: string): Promise<ListVolumesResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/list`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const getVolume = async (token: string, volumeId: string): Promise<Volume> => {
  const response = await fetch(`${getBaseUrl()}/volume/${volumeId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const createVolume = async (token: string, payload: CreateVolumePayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const resizeVolume = async (token: string, payload: ResizeVolumePayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/${payload.volumeId}/resize`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ newSize: payload.newSize }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const attachVolume = async (token: string, payload: AttachVolumePayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/${payload.volumeId}/attach`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      instanceId: payload.instanceId,
      mountPath: payload.mountPath,
    }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const detachVolume = async (token: string, payload: DetachVolumePayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/${payload.volumeId}/detach`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ instanceId: payload.instanceId }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const cloneVolume = async (token: string, payload: CloneVolumePayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/${payload.sourceVolumeId}/clone`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: payload.name,
      snapshotId: payload.snapshotId,
      storageClassId: payload.storageClassId,
    }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const deleteVolume = async (token: string, volumeId: string): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/${volumeId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

// Volume Metrics API
export const getVolumeMetricsHistory = async (
  token: string,
  volumeId: string,
  hours: number = 24
): Promise<MetricsHistoryPoint[]> => {
  const response = await fetch(`${getBaseUrl()}/volume/${volumeId}/metrics?hours=${hours}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

// Volume Activity Logs
export const getVolumeActivityLogs = async (token: string, volumeId: string): Promise<VolumeActivityLog[]> => {
  const response = await fetch(`${getBaseUrl()}/volume/${volumeId}/activity`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

// Snapshot API Functions
export const listSnapshots = async (token: string, volumeId?: string): Promise<ListSnapshotsResponse> => {
  const url = volumeId
    ? `${getBaseUrl()}/volume/${volumeId}/snapshots`
    : `${getBaseUrl()}/snapshot/list`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const getSnapshot = async (token: string, snapshotId: string): Promise<VolumeSnapshot> => {
  const response = await fetch(`${getBaseUrl()}/snapshot/${snapshotId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const createSnapshot = async (token: string, payload: CreateSnapshotPayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/${payload.volumeId}/snapshot`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: payload.name,
      description: payload.description,
      labels: payload.labels,
    }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const restoreSnapshot = async (token: string, payload: RestoreSnapshotPayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/snapshot/${payload.snapshotId}/restore`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      restoreType: payload.restoreType,
      newVolumeName: payload.newVolumeName,
      newStorageClassId: payload.newStorageClassId,
    }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const deleteSnapshot = async (token: string, snapshotId: string): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/snapshot/${snapshotId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

// Schedule API Functions
export const listSchedules = async (token: string, volumeId?: string): Promise<ListSchedulesResponse> => {
  const url = volumeId
    ? `${getBaseUrl()}/volume/${volumeId}/schedules`
    : `${getBaseUrl()}/schedule/list`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const createSchedule = async (token: string, payload: CreateSchedulePayload): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/volume/${payload.volumeId}/schedule`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: payload.name,
      frequency: payload.frequency,
      cronExpression: payload.cronExpression,
      retentionCount: payload.retentionCount,
      retentionDays: payload.retentionDays,
      snapshotPrefix: payload.snapshotPrefix,
    }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const updateSchedule = async (
  token: string,
  scheduleId: string,
  updates: Partial<CreateSchedulePayload>
): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/schedule/${scheduleId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const toggleSchedule = async (token: string, scheduleId: string, enabled: boolean): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/schedule/${scheduleId}/toggle`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const deleteSchedule = async (token: string, scheduleId: string): Promise<VolumeOperationResponse> => {
  const response = await fetch(`${getBaseUrl()}/schedule/${scheduleId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

// Storage Class API
export const listStorageClasses = async (token: string): Promise<StorageClass[]> => {
  const response = await fetch(`${getBaseUrl()}/storage-class/list`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};
